const getPredictionMainText = (prediction) =>
  prediction?.structuredFormat?.mainText?.text ||
  prediction?.text?.text?.split(',')[0] ||
  '';

const hasInlineCoordinates = (prediction) => {
  const structured = prediction?.structuredFormat;
  return structured?.lat !== undefined && structured?.lng !== undefined;
};

/**
 * @typedef {Object} PredictionText
 * @property {string} [text]
 *
 * @typedef {Object} StructuredPrediction
 * @property {PredictionText} [mainText]
 * @property {PredictionText} [secondaryText]
 *
 * @typedef {Object} PlacePrediction
 * @property {string[]} [types]
 * @property {PredictionText} [text]
 * @property {StructuredPrediction} [structuredFormat]
 *
 * @typedef {Object} PlaceSuggestion
 * @property {PlacePrediction} [placePrediction]
 */

/**
 * A suggestion is selectable when we can actually resolve it to a location:
 * either it carries a placeId we can send to the place-details endpoint, or it
 * already carries inline coordinates (the shape ZIP lookups return).
 *
 * City-, ZIP- and route-level picks are all allowed; only entries we cannot
 * turn into coordinates are rejected.
 *
 * @param {PlaceSuggestion | PlacePrediction | null | undefined} suggestion
 * @returns {boolean}
 */
export const isSelectableAddressSuggestion = (suggestion) => {
  const prediction = suggestion?.placePrediction || suggestion;
  if (!prediction) return false;

  if (prediction.placeId) return true;
  if (hasInlineCoordinates(prediction)) return true;

  return false;
};

/**
 * Returns the actionable validation message for a rejected suggestion.
 *
 * @param {PlaceSuggestion | PlacePrediction | null | undefined} suggestion
 * @returns {string}
 */
export const getAddressSuggestionValidationMessage = (suggestion) => {
  if (isSelectableAddressSuggestion(suggestion)) return '';

  const prediction = suggestion?.placePrediction || suggestion;
  if (getPredictionMainText(prediction)) {
    return "We couldn't look up this suggestion. Please choose another one.";
  }

  return 'Please enter a valid address.';
};
